import {
  addToaster,
  createTableView,
  editTableView,
  ErrorResponse,
  fetchAllTableViews,
  hideLoader,
  showLoader,
  TableFilterObjectBody,
  TableViewResponseType,
} from '@app-store'
import {
  ConfirmationModal,
  CustomButton,
  DropdownType,
  Modal,
  SelectDropDown,
} from '@ui-kit'
import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import {
  RootState,
  useAppDispatch,
  useAppSelector,
} from '../../utils/agent.store'
import { DropDownOption } from '../../types/types'
import { BUTTONS } from '../../utils/constants'
import { RECORD_STATUS } from '../../constants/form.constants'
import _ from 'lodash'
import TableViewOptionBox from './TableViewOptionBox'
import SaveViewModal from './SaveViewModal'
import {
  TableViewDropdownPropsType,
  TableViewType,
} from '../../types/userPreferences.types'
import { AxiosError } from 'axios'
import { v4 as uuid } from 'uuid'
import { STRINGS } from '../../utils/strings'
import { ActionsDropDown } from '../queue-config/ActionsPopUp'
import useWatchedRef from '../../hooks/useWatchedRef'

const TableViewsDropdown = ({
  apiFilter,
  setApiFilter,
  setIsMounted,
  contextType,
  id,
  isEntityView = false,
  setDependencyKey,
}: TableViewDropdownPropsType) => {
  const userId =
    useAppSelector((state: RootState) => state?.authUser?.user?.id) ?? -1
  const dispatch = useAppDispatch()

  const [viewOptions, setViewOptions] = useState<
    DropdownType<DropDownOption>[]
  >([])

  // Get current filter value based on view type
  const currentFilter = useMemo(() => {
    return isEntityView && apiFilter && 'current' in apiFilter
      ? apiFilter.current
      : (apiFilter as TableFilterObjectBody | undefined)
  }, [
    isEntityView,
    apiFilter && 'current' in apiFilter ? apiFilter.current : apiFilter,
  ])

  const [currentView, setCurrentView] = useState<TableViewType>({
    filter_body: currentFilter,
  })
  const currentViewRef = useRef<TableViewType>()
  const viewOptionsRef = useRef<DropdownType<DropDownOption>[]>([])

  const [modal, setModal] = useState<{ type: string; state: boolean }>({
    type: '',
    state: false,
  })

  const deleteOptionRef = useRef<DropdownType<DropDownOption> | null>(null)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [isButtonVisible, setIsButtonVisible] = useWatchedRef<boolean>(true)

  const UpdateFilterOptions = [
    {
      label: BUTTONS.SAVE,
      value: BUTTONS.SAVE,
      key: BUTTONS.SAVE,
    },
    {
      label: BUTTONS.SAVE_AS,
      value: BUTTONS.SAVE_AS,
      key: BUTTONS.SAVE_AS,
    },
  ]

  const fetchAllViews = async () => {
    try {
      const payload = {
        subject_type: 'USER',
        subject_id: userId,
        context_type: contextType,
        context_id: id,
      }
      const options = []
      options?.push({
        label: STRINGS.DEFAULT_VIEW,
        filter_body: {},
        value: '-1',
        record_status: RECORD_STATUS.INACTIVE,
        renderedComponent: (
          <TableViewOptionBox
            option={{
              label: STRINGS.DEFAULT_VIEW,
              filter_body: {} as DropDownOption,
              value: '-1',
              record_status: RECORD_STATUS.INACTIVE,
            }}
            handleDelete={handleDeleteIconClick}
            handleEdit={handleEditIconClick}
          />
        ),
      })
      const response = await fetchAllTableViews(payload)
      if (response?.data?.length === 0) {
        setCurrentView({ id: -1, name: 'Default' })
        setApiFilter({})
        setIsMounted?.()
        setViewOptions(options as unknown as DropdownType<DropDownOption>[])
        return
      }
      const tempOptions = response?.data?.map(
        (item: TableViewResponseType) => ({
          label: item?.name,
          filter_body: item?.filter_body,
          value: item?.id?.toString(),
          record_status: item?.record_status,
          renderedComponent: (
            <TableViewOptionBox
              option={{
                label: item?.name,
                filter_body: item?.filter_body as ReactNode,
                value: item?.id?.toString(),
                record_status: item?.record_status,
              }}
              handleDelete={handleDeleteIconClick}
              handleEdit={handleEditIconClick}
            />
          ),
        })
      )
      options?.push(...tempOptions)
      const defaultOption = options?.find(
        (item: DropdownType<DropDownOption>) =>
          item?.record_status === RECORD_STATUS.ACTIVE
      )
      if (!isEntityView) {
        setIsMounted?.()
      }
      if (defaultOption?.filter_body) {
        setApiFilter(defaultOption?.filter_body)
        setCurrentView({
          id: defaultOption?.value,
          name: defaultOption?.label,
          filter_body: defaultOption?.filter_body,
        })
      } else {
        setCurrentView({ id: -1, name: 'Default' })
        setApiFilter({})
      }
      setViewOptions(options as unknown as DropdownType<DropDownOption>[])
    } catch (err) {
      console.error(err)
      const response = (err as AxiosError)?.response?.data as ErrorResponse
      dispatch(
        addToaster({
          message: response?.message ?? STRINGS.ERROR_FETCHING_VIEWS,
          type: 'error',
          id: uuid(),
        })
      )
      setIsMounted?.()
    } finally {
      if (isEntityView) setIsMounted?.()
    }
  }

  const handleViewChange = async (value: DropdownType<DropDownOption>) => {
    if (!value) return
    if (value?.value === currentViewRef?.current?.id?.toString()) return
    try {
      dispatch(showLoader())
      setApiFilter(value?.filter_body as TableFilterObjectBody)
      const viewObj = {
        name: value?.label,
        id: parseInt(value?.value),
        filter_body: value?.filter_body as TableFilterObjectBody,
      }
      setCurrentView(viewObj as TableViewType)
      let viewId = value?.value
      if (value?.value === '-1') {
        const targetViewId = viewOptionsRef?.current?.find(
          (item: DropdownType<DropDownOption>) =>
            item?.record_status === RECORD_STATUS.ACTIVE
        )?.value
        viewId = targetViewId ?? '0'
      }
      const payload = {
        subject_type: 'USER',
        subject_id: userId,
        context_type: contextType,
        context_id: id,
        record_status:
          value?.value === '-1' ? RECORD_STATUS.INACTIVE : RECORD_STATUS.ACTIVE,
      }
      await editTableView(parseInt(viewId ?? '-1'), payload)

      if (isEntityView) {
        setDependencyKey?.((prev: number) => prev + 1)
      } else {
        setIsMounted?.()
      }
    } catch (err) {
      console.error(err)
    } finally {
      dispatch(hideLoader())
    }
  }

  const handleSave = async (name: string) => {
    try {
      dispatch(showLoader())
      const current = currentFilter
      const payload = {
        subject_type: 'USER',
        subject_id: userId,
        context_type: contextType,
        context_id: id,
        name: name?.trim(),
        filter_body: current,
      }
      if (modal?.type === BUTTONS.CREATE || modal?.type === BUTTONS?.SAVE_AS) {
        await createTableView(payload)
      } else if (modal?.type === BUTTONS.SAVE) {
        await editTableView(currentViewRef?.current?.id ?? -1, payload)
      }
      await fetchAllViews()
      setModal({ state: false, type: '' })
    } catch (error) {
      console.error(error)
      const response = (error as AxiosError)?.response?.data as ErrorResponse
      dispatch(
        addToaster({
          message: response?.message ?? STRINGS.ERROR_SAVING_FILTER,
          type: 'error',
          id: uuid(),
        })
      )
    } finally {
      dispatch(hideLoader())
    }
  }

  const handleEditIconClick = (
    e: React.MouseEvent<SVGSVGElement, MouseEvent>,
    option: DropdownType<DropDownOption>
  ) => {
    if (currentViewRef?.current?.id === parseInt(option?.value)) return
    e?.stopPropagation()
    setApiFilter(option?.filter_body as TableFilterObjectBody)
    if (isEntityView && setDependencyKey) {
      setDependencyKey((prev: number) => prev + 1)
    }
    const viewObj = {
      name: option?.label,
      id: parseInt(option?.value),
      filter_body: option?.filter_body as TableFilterObjectBody,
    }
    setCurrentView(viewObj)
  }

  const handleDeleteIconClick = (
    e: React.MouseEvent<SVGSVGElement, MouseEvent>,
    option: DropdownType<DropDownOption>
  ) => {
    e.stopPropagation()
    deleteOptionRef.current = option
    setIsDeleteDialogOpen(true)
  }

  const handleDeleteView = async (option: DropdownType<DropDownOption>) => {
    try {
      dispatch(showLoader())
      const payload = {
        subject_type: 'USER',
        subject_id: userId,
        context_type: contextType,
        context_id: id,
        record_status: RECORD_STATUS.DELETED,
      }
      try {
        await editTableView(parseInt(option?.value), payload)
      } catch (error) {
        console.error(error)
        const response = (error as AxiosError)?.response?.data as ErrorResponse
        dispatch(
          addToaster({
            message: response?.message ?? STRINGS.ERROR_DELETING_VIEW,
            type: 'error',
            id: uuid(),
          })
        )
      }
      //NOTE : IF VIEW TO BE DELETED IS NOT AN ACTIVE VIEW, THEN MAKE ACTIVE VIEW TO INACTIVE
      const targetViewId = viewOptionsRef?.current?.find(
        (item: DropdownType<DropDownOption>) =>
          item?.record_status === RECORD_STATUS.ACTIVE
      )?.value
      if (option?.value !== targetViewId) {
        if (targetViewId) {
          const payloadToMakeInactive = {
            subject_type: 'USER',
            subject_id: userId,
            context_type: contextType,
            context_id: id,
            record_status: RECORD_STATUS.INACTIVE,
          }
          await editTableView(
            parseInt(targetViewId ?? '-1'),
            payloadToMakeInactive
          )
        }
      }
      await fetchAllViews()
      setDependencyKey?.((prev: number) => prev + 1)
      setIsDeleteDialogOpen(false)
      deleteOptionRef.current = null
    } catch (error) {
      console.error(error)
    } finally {
      dispatch(hideLoader())
    }
  }

  const handleOpenActionModal = async (): Promise<DropdownType<unknown>[]> => {
    return UpdateFilterOptions
  }

  useEffect(() => {
    const val = !_.isEqual(currentFilter ?? {}, currentView?.filter_body ?? {})
    setIsButtonVisible(val)
  }, [currentView, currentFilter])

  useEffect(() => {
    if (id === 0) return
    fetchAllViews()
  }, [])

  useEffect(() => {
    currentViewRef.current = currentView
  }, [currentView])

  useEffect(() => {
    viewOptionsRef.current = viewOptions
  }, [viewOptions])

  return (
    <div className="flex flex-row gap-2">
      <div className="flex w-full font-light text-sm truncate block bg-accent-100 rounded text-center text-error-500">
        <SelectDropDown
          label={''}
          options={viewOptions}
          name={`view_dropdown`}
          onChange={handleViewChange}
          placeholder={STRINGS.DEFAULT_VIEW}
          className="h-[2rem] flex"
          isCrossable={false}
          isBordered={false}
          defaultValue={currentView?.id?.toString()}
          checkPosition={false}
        />
      </div>
      {isButtonVisible?.current &&
        (currentView?.id === -1 ? (
          <CustomButton
            dataQaId="review-button"
            type="button"
            color="primary"
            onClick={() => setModal({ type: BUTTONS.CREATE, state: true })}
            className="min-w-[5rem]"
            isFilled
          >
            <span>{BUTTONS.SAVE_FILTER}</span>
          </CustomButton>
        ) : (
          <ActionsDropDown
            handleOpenOptions={handleOpenActionModal}
            handleValueSelect={async (selectedOption) =>
              setModal({ type: selectedOption?.value ?? '', state: true })
            }
            initialOptions={UpdateFilterOptions}
            label={BUTTONS.UPDATE_FILTER}
            buttonCss="bg-primary-500 text-accent-0"
            svgCss="text-accent-0"
          />
        ))}
      {modal?.state && (
        <SaveViewModal
          modal={modal}
          setModal={setModal}
          handleSave={handleSave}
          currentView={currentView}
        />
      )}
      <Modal
        isOpen={isDeleteDialogOpen}
        closeModal={() => {
          setIsDeleteDialogOpen(false)
          deleteOptionRef.current = null
        }}
      >
        <ConfirmationModal
          message={`${STRINGS.DO_YOU_WANT_TO_DELETE_VIEW}${
            deleteOptionRef.current?.label ?? ''
          }${STRINGS.VIEW_QUESTION_MARK}`}
          handleNo={() => {
            setIsDeleteDialogOpen(false)
            deleteOptionRef.current = null
          }}
          handleYes={async () => {
            const opt = deleteOptionRef.current
            if (opt) await handleDeleteView(opt)
          }}
        />
      </Modal>
    </div>
  )
}

export default TableViewsDropdown
